import API from 'src/common/utils/api';

/** Account from Cosmos admin endpoint */
export interface CosmosAccountInfo {
  id: string;
  name: string;
  email: string;
  office_phone: string;
  timezone: string;
}

/** Time range for office hours */
export interface CosmosOfficeHoursRange {
  from: string; // "HH:MM"
  to: string; // "HH:MM"
}

/** Office hours configuration for a single day */
export interface CosmosOfficeHoursDay {
  enabled: boolean;
  ranges: CosmosOfficeHoursRange[];
}

/** Office hours for all days of the week */
export interface CosmosOfficeHours {
  monday: CosmosOfficeHoursDay;
  tuesday: CosmosOfficeHoursDay;
  wednesday: CosmosOfficeHoursDay;
  thursday: CosmosOfficeHoursDay;
  friday: CosmosOfficeHoursDay;
  saturday: CosmosOfficeHoursDay;
  sunday: CosmosOfficeHoursDay;
}

/** User from Cosmos admin endpoint (via account's user_set) */
export interface CosmosUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_account_manager: boolean;
}

/** Account with users from Cosmos admin endpoint (without basic_info) */
interface CosmosAccountWithUsers extends CosmosAccountInfo {
  user_set: CosmosUser[];
}

/** Paginated response from Cosmos account_admin endpoint */
interface CosmosAccountListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CosmosAccountInfo[];
}

/** IVR Option from Cosmos */
export interface CosmosIvrOption {
  display_name: string;
  key: string;
  is_root: boolean;
  ivr_option_enabled: boolean;
  message_type: string;
  audio_message: Record<string, CosmosIvrAudioMessage>;
  options: CosmosIvrOption[];
  response_tag: { name: string; color: string } | null;
}

/** IVR Audio Message from Cosmos */
export interface CosmosIvrAudioMessage {
  ivr_option: string;
  selection_message: string | null;
  selection_audio_link: string | null;
  ivr_selection_audio_file: string | null;
  message: string | null;
  audio_link: string | null;
  ivr_audio_file: string | null;
  language: 'en' | 'es' | 'en_US' | 'es_ES';
}

/** IVR Greeting from Cosmos */
export interface CosmosIvrGreeting {
  id: string;
  greeting_type: number;
  ivr_greeting_message: string | null;
  ivr_greeting_audio_file: string | null;
  active: boolean;
}

/** IVR Setting from Cosmos */
export interface CosmosIvrSetting {
  id: number;
  ivr_enabled: boolean;
  language: 'en' | 'es';
}

/** Full IVR configuration for an account */
export interface CosmosIvrConfig {
  enabled: boolean;
  setting: CosmosIvrSetting | null;
  options: CosmosIvrOption[];
  greetings: CosmosIvrGreeting[];
}

/**
 * Search accounts by name or UUID.
 * Uses the account_admin endpoint with search and basic_info parameters.
 */
export const searchCosmosAccounts = async (query: string): Promise<CosmosAccountInfo[]> => {
  if (!query.trim()) return [];

  const response = await API.get<CosmosAccountListResponse>('/account_admin', {
    params: { search: query, page: 1, page_size: 10, basic_info: false },
  });
  return response.data.results ?? [];
};

/**
 * Get account by ID (after selection).
 * Returns null if not found (404).
 */
export const fetchCosmosAccount = async (accountId: string): Promise<CosmosAccountInfo | null> => {
  try {
    const response = await API.get<CosmosAccountInfo>(`/account_admin/${accountId}`, {
      params: { basic_info: false },
    });
    return response.data;
  } catch (error) {
    const axiosErr = error as { response?: { status?: number } };
    if (axiosErr?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Get account by ID with users (no basic_info).
 * Returns account info and user_set array.
 */
export const fetchCosmosAccountWithUsers = async (
  accountId: string
): Promise<{ account: CosmosAccountInfo; users: CosmosUser[] } | null> => {
  try {
    const response = await API.get<CosmosAccountWithUsers>(`/account_admin/${accountId}`);
    const { user_set, ...account } = response.data;
    return {
      account,
      users: user_set ?? [],
    };
  } catch (error) {
    const axiosErr = error as { response?: { status?: number } };
    if (axiosErr?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Fetch IVR configuration from Cosmos for a specific account.
 * Requires `account_id` query param on IVR endpoints (new backend feature).
 */
export const fetchCosmosIvrConfig = async (accountId: string): Promise<CosmosIvrConfig> => {
  try {
    const [settingRes, optionsRes, greetingsRes] = await Promise.all([
      API.get<CosmosIvrSetting>('/ivr/setting/fetch', { params: { account_id: accountId } }),
      API.get<CosmosIvrOption[]>('/ivr/option', { params: { account_id: accountId } }),
      API.get<CosmosIvrGreeting[]>('/ivr/greeting', { params: { account_id: accountId } }),
    ]);

    return {
      enabled: settingRes.data?.ivr_enabled ?? false,
      setting: settingRes.data ?? null,
      options: optionsRes.data ?? [],
      greetings: greetingsRes.data ?? [],
    };
  } catch (error) {
    const axiosErr = error as { response?: { status?: number } };
    // If IVR is not configured for this account, return empty config
    if (axiosErr?.response?.status === 404) {
      return {
        enabled: false,
        setting: null,
        options: [],
        greetings: [],
      };
    }
    throw error;
  }
};

/** Response from account_admin endpoint (subset of fields we care about) */
interface CosmosAccountAdminResponse {
  office_hours?: CosmosOfficeHours;
}

/**
 * Fetch office hours from Cosmos for a specific account.
 * Uses the /account_admin endpoint which includes office_hours in the response.
 * Returns null if no office hours are configured or if the account is not found.
 */
export const fetchCosmosOfficeHours = async (
  accountId: string
): Promise<CosmosOfficeHours | null> => {
  try {
    const response = await API.get<CosmosAccountAdminResponse>(`/account_admin/${accountId}`);
    return response.data?.office_hours ?? null;
  } catch (error) {
    const axiosErr = error as { response?: { status?: number } };
    // Return null for 404 (not found) - allows wizard to continue with default schedule
    if (axiosErr?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/** Payload to create a user in Cosmos */
export interface CreateCosmosUserPayload {
  account: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  password: string;
  is_account_manager?: boolean;
  is_active?: boolean;
}

/** Response from creating a user in Cosmos */
export interface CreateCosmosUserResponse {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_account_manager: boolean;
}

/**
 * Create a new user in Cosmos for the given account.
 * After creation, the user can be added to Interstellar.
 */
export const createCosmosUser = async (
  payload: CreateCosmosUserPayload
): Promise<CreateCosmosUserResponse> => {
  const response = await API.post<CreateCosmosUserResponse>('/users', {
    ...payload,
    is_active: payload.is_active ?? true,
    is_account_manager: payload.is_account_manager ?? false,
  });
  return response.data;
};

/** Phone widget features Cosmos lets an account turn on, as `AccountSettings.enabled_phone_features`. */
export type CosmosPhoneFeature = 'conference' | 'ivr' | 'call_whisper' | 'unavailable_reasons';

/** Account settings that decide which phone features telescope shows */
export interface CosmosPhoneSettings {
  has_soft_phone: boolean;
  enabled_phone_features: CosmosPhoneFeature[];
}

/**
 * Get the softphone and phone features of a Cosmos account. An account without
 * settings answers the defaults (no softphone, no features).
 */
export const fetchCosmosPhoneSettings = async (accountId: string): Promise<CosmosPhoneSettings> => {
  const response = await API.get<CosmosPhoneSettings>(`/account_admin/${accountId}/phone_settings`);
  return response.data;
};

/**
 * Update the softphone and/or phone features of a Cosmos account, creating its
 * settings when it has none. Only the fields sent are written.
 */
export const updateCosmosPhoneSettings = async (
  accountId: string,
  payload: Partial<CosmosPhoneSettings>
): Promise<CosmosPhoneSettings> => {
  const response = await API.patch<CosmosPhoneSettings>(
    `/account_admin/${accountId}/phone_settings`,
    payload
  );
  return response.data;
};
